import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, ShieldCheck, TriangleAlert, Lock, ArrowRightLeft, Undo2, Search, Phone, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLookup, LookupBar, BookingCard, TransferPanel, CancelForm } from "@/pages/admin/TicketDesk";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/format";
import { api, errMsg } from "@/lib/api";

const RECON_REASONS = [
  ["customer_request", "Customer request", "Charge on fare: 10% if 12h+ before, 20% at 6-12h, 50% under 6h. Insurance returned in full."],
  ["trip_not_performed", "Trip not performed", "0% charge - bus did not run; fare + insurance returned"],
  ["trip_not_operated", "Trip not operated", "0% charge - online tickets only, full refund"],
];

const GW_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  failed: "bg-rose-50 text-rose-700",
  pending: "bg-amber-50 text-amber-700",
};

export default function PaymentCheck() {
  const l = useLookup();
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [checking, setChecking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [mode, setMode] = useState("code");
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const searchPhone = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setSearching(true); setResults(null);
    try {
      const { data } = await api.get("/tickets/by-phone", { params: { phone: phone.trim() } });
      setResults(data);
      if (!data.length) toast.message("No bookings found for that mobile number");
    } catch (err) { toast.error(errMsg(err)); } finally { setSearching(false); }
  };
  const pickResult = (code) => { l.setCode(code); l.find(code); setResults(null); };

  const b = l.b;
  useEffect(() => { setRes(null); }, [b?.code]);

  const reasons = user?.role === "admin"
    ? [["teller_mistake", "Teller mistake (on behalf of teller)", "0% - full refund to the customer"], ...RECON_REASONS]
    : RECON_REASONS;

  const needsConfirm = b && ["pending_payment", "payment_failed"].includes(b.status);
  const confirmed = b && ["confirmed", "boarded"].includes(b.status);

  const check = async () => {
    if (!b) return;
    setChecking(true); setRes(null);
    try {
      const { data } = await api.post(`/admin/payments/${b.code}/reconcile`);
      setRes(data);
      await l.find(b.code);
      toast.success(`Hubtel reports: ${data.gateway_status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };

  const forceConfirm = async () => {
    if (!b) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/admin/payments/${b.code}/reconcile`, {}, { params: { force: true } });
      setRes(data);
      await l.find(b.code);
      if (data.full) toast.warning("The bus is full — no seat was free, so the payment has been refunded to the customer.");
      else if (["confirmed", "boarded"].includes(data.status)) toast.success("Ticket confirmed — seat allocated and the ticket SMS has been sent.");
      else toast.message(`Status: ${data.status} · payment ${data.payment_status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setConfirming(false); }
  };

  return (
    <div className="space-y-5" data-testid="payment-check">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Payment check &amp; reconcile</h2>
        <p className="max-w-3xl text-sm text-slate-500">
          When a customer was charged (MoMo / card at any channel) but the ticket did not get booked, look it up with the
          booking code / payment reference, verify the payment with Hubtel, then confirm the ticket, move it to a free seat,
          change the travel date, or refund it online.
        </p>
      </div>

      <div className="card-soft space-y-3 p-4" data-testid="recon-search">
        <div className="inline-flex rounded-xl bg-slate-100 p-1 text-sm font-semibold">
          <button type="button" onClick={() => { setMode("code"); setResults(null); }} className={`rounded-lg px-3 py-1.5 transition-colors ${mode === "code" ? "bg-white text-[#0052FF] shadow-sm" : "text-slate-500"}`} data-testid="recon-mode-code"><Ticket className="mr-1.5 inline h-4 w-4" />Ticket number</button>
          <button type="button" onClick={() => { setMode("phone"); setResults(null); }} className={`rounded-lg px-3 py-1.5 transition-colors ${mode === "phone" ? "bg-white text-[#0052FF] shadow-sm" : "text-slate-500"}`} data-testid="recon-mode-phone"><Phone className="mr-1.5 inline h-4 w-4" />Mobile number</button>
        </div>
        {mode === "code" ? (
          <LookupBar l={l} testid="recon" />
        ) : (
          <form onSubmit={searchPhone} className="flex max-w-lg gap-2">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Traveller's mobile e.g. 024 123 4567" className="font-mono" inputMode="tel" data-testid="recon-phone-input" />
            <Button type="submit" disabled={searching} className="bg-[#0052FF] hover:bg-[#0041CC]" data-testid="recon-phone-search-button">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
          </form>
        )}
        {results && results.length > 0 && (
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200" data-testid="recon-phone-results">
            {results.map((r) => (
              <button key={r.code} type="button" onClick={() => pickResult(r.code)} className="flex w-full items-center justify-between gap-3 p-3 text-left text-sm hover:bg-slate-50 btn-press" data-testid={`recon-result-${r.code}`}>
                <span className="min-w-0">
                  <span className="font-mono font-bold">{r.code}</span> <span className="text-slate-500">· {r.passengers.join(", ")}</span>
                  <span className="block text-xs text-slate-500">{r.route}{r.departure ? ` · ${r.departure.slice(0, 10)} ${fmtTime(r.departure)}` : ""}{r.seats.length ? ` · seats ${r.seats.join(", ")}` : ""}</span>
                </span>
                <span className={`chip shrink-0 capitalize ${r.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : ["pending_payment", "payment_failed"].includes(r.status) ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{r.status.replace("_", " ")}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {b && <BookingCard b={b} />}

      {b && b.payment_method !== "cash" && (
        <div className="card-soft space-y-4 p-4" data-testid="recon-status-panel">
          <div className="flex flex-wrap items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#0052FF]" />
            <span className="font-display font-bold">Hubtel payment status</span>
            {res && !confirmed && <span className={`chip capitalize ${GW_STYLE[res.gateway_status] || "bg-slate-100 text-slate-600"}`} data-testid="recon-gateway-status">Hubtel: {res.gateway_status}</span>}
            <span className="chip bg-slate-100 capitalize text-slate-600" data-testid="recon-booking-status">Ticket: {b.status.replace("_", " ")}</span>
            <span className="chip bg-slate-100 capitalize text-slate-600" data-testid="recon-payment-status">Payment: {b.payment_status}</span>
          </div>

          {confirmed ? (
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-700" data-testid="recon-confirmed-note">
              <CheckCircle2 className="h-4 w-4" />This ticket is confirmed — seat(s) {b.passengers.map((p) => p.seat).join(", ")} allocated and the SMS has been sent.
            </div>
          ) : needsConfirm ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={check} disabled={checking || confirming} className="rounded-xl" data-testid="recon-check-button">
                  {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}Check payment with Hubtel
                </Button>
                <Button type="button" onClick={forceConfirm} disabled={confirming || checking} className="rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="recon-confirm-button">
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Confirm ticket (payment verified)
                </Button>
              </div>
              <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="recon-confirm-hint">
                <TriangleAlert className="mr-1 inline h-3.5 w-3.5" />
                First press <b>Check payment with Hubtel</b>. If Hubtel shows the payment succeeded, press <b>Confirm ticket</b> — the seat is
                allocated (a free seat is picked automatically if the original was taken), the sale is recorded and the ticket SMS is sent.
                If the bus is already full the amount is refunded instead.
              </p>
            </>
          ) : (
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500" data-testid="recon-no-action-note">
              This booking is <b className="capitalize">{b.status.replace("_", " ")}</b> — it is not an unconfirmed online payment. Use the tabs below to reseat, change the date, or refund it.
            </div>
          )}
        </div>
      )}

      {b && (
        <Tabs defaultValue="reseat" className="space-y-4" data-testid="recon-actions">
          <TabsList className="rounded-xl">
            <TabsTrigger value="reseat" data-testid="recon-reseat-tab"><ArrowRightLeft className="mr-1.5 h-4 w-4" />Reseat / change date</TabsTrigger>
            <TabsTrigger value="refund" data-testid="recon-refund-tab"><Undo2 className="mr-1.5 h-4 w-4" />Refund</TabsTrigger>
          </TabsList>
          <TabsContent value="reseat" className="space-y-3">
            <p className="text-sm text-slate-500">Move the passenger(s) to an empty seat on another departure of the same journey (a different time or date). The fare already paid stays the same.</p>
            <TransferPanel b={b} onDone={l.setB} />
          </TabsContent>
          <TabsContent value="refund" className="space-y-3">
            {["confirmed", "reserved"].includes(b.status) && b.payment_method === "cash" && (
              <div className="card-soft flex items-start gap-3 p-4" data-testid="recon-cash-notice">
                <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div className="text-sm text-slate-600">This is a <b>cash-paid</b> ticket. Cash refunds can only be handed back by the selling <b>teller at the counter</b>.</div>
              </div>
            )}
            {["confirmed", "reserved"].includes(b.status) && b.payment_method !== "cash"
              ? <CancelForm b={b} reasons={reasons} onDone={l.setB} />
              : !["confirmed", "reserved"].includes(b.status) && <p className="card-soft p-4 text-sm text-slate-500" data-testid="recon-refund-not-allowed">A {b.status.replace("_", " ")} ticket cannot be refunded here.</p>}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
